import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  addEnquiry,
  getCountryCode,
  varifyCaptcha,
} from "../../redux/Thunk/providerThunk";
import {
  useGoogleReCaptcha,  
} from "react-google-recaptcha-v3";

const EnqForm = ({ onClose, mealsData }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    subject: "",
    other: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const { userId, isProviderLoading } = useSelector((state) => state.providers);
  const user_id = localStorage.getItem("ngIGj7DweAxsKzOQvMxTJQ3wzjmaGtyD");
  const [isOther, setIsOther] = useState(false);
  const dispatch = useDispatch();
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      // Only digits allow
      const numericValue = value.replace(/\D/g, "");
      setForm({ ...form, [name]: numericValue });
      return;
    }

    setForm({ ...form, [name]: value });

    if (name === "subject") setIsOther(value === "Other");
  };

  const [countryCode, setCountryCode] = useState("");
  const [countryCodes, setCountryCodes] = useState([]);

  useEffect(() => {
    dispatch(getCountryCode())
      .unwrap()
      .then((res) => {
        // console.log("Country codes fetched:", res.data);

        const formatted = res.data.map((c) => ({
          value: c.id,
          label: `${c.country_code} ${c.country_name.toLowerCase()}`,
        }));

        setCountryCodes(formatted);
      });
  }, [dispatch]);

  // ✅ Proper validation function
  const validateFields = () => {
    let newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = "Please enter your name";
    }

    if (!countryCode) {
      newErrors.mobile = "Please select country code";
    } else if (!form.mobile.trim()) {
      newErrors.mobile = "Please enter mobile number";
    } else if (!/^\d+$/.test(form.mobile)) {
      newErrors.mobile = "Mobile number must contain only digits";
    } else if (form.mobile.length !== 10) {
      newErrors.mobile = "Mobile number must be exactly 10 digits";
    }

    if (!form.email.trim()) {
      newErrors.email = "Please enter email address";
    }

    if (!form.subject.trim()) {
      newErrors.subject = "Please select meal type";
    }

    if (isOther && !form.other.trim()) {
      newErrors.other = "Please enter other meal type";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFields()) return;

    if (!executeRecaptcha) {
      toast.error("reCAPTCHA not ready");
      return;
    }

    try {
      const captchaToken = await executeRecaptcha("submit");

      if (!captchaToken) {
        toast.error("Captcha failed. Try again.");
        return;
      }

      // console.log("Captcha Token:", captchaToken);

      // ✅ STEP 2
      const captchaRes = await dispatch(
        varifyCaptcha({
          token: captchaToken,
          action: "submit",
        }),
      ).unwrap();

      // console.log("Captcha Response:", captchaRes);

      // ✅ STEP 3
      if (!captchaRes || !captchaRes.success) {
        toast.error("Captcha verification failed");
        return;
      }

      const payload = {
        user_id: userId || user_id,
        country_id: countryCode,
        customer_name: form.name,
        mobile: form.mobile,
        email: form.email,
        meal_id: form.subject === "Other" ? null : form.subject,
        subject: form.subject === "Other" ? form.other : null,
        description: form.description,
      };

      const res = await dispatch(addEnquiry(payload)).unwrap();

      if (!res.success) {
        toast.error(res.message);
        return;
      }

      toast.success(res.message || "Enquiry sent successfully");
      onClose();
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-[2px] p-4"
      onClick={onClose}
    >
      {/* MOBILE CLOSE */}
      <button
        onClick={onClose}
        className="sm:hidden fixed top-5 right-5 z-[999] bg-white rounded-full p-2 shadow-lg hover:scale-110 transition"
      >
        <X className="w-5 h-5 text-gray-800" />
      </button>

      {/* MODAL */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white/90  w-full max-w-6xl rounded-3xl shadow-2xl flex flex-col sm:flex-row overflow-hidden animate-fadeIn"
      >
        {/* LEFT SIDE */}
        <div className="sm:w-1/2 bg-gradient-to-tr from-orange-400 via-white-200 to-orange-300 relative flex flex-col items-center justify-center p-6">
          <div className="absolute w-32 h-32 bg-white/30 rounded-full -top-10 -left-10 animate-pulse-slow"></div>
          <div className="absolute w-48 h-48 bg-white/20 rounded-full bottom-10 right-10 animate-pulse-slower"></div>
          <h3 className="text-white text-3xl font-bold z-10 text-center">
            Quick Inquiry
          </h3>
          <p className="text-white/80 mt-2 text-center">
            Fill the form and we will contact you soon!
          </p>
        </div>

        {/* RIGHT SIDE - FORM */}
        <div className="sm:w-1/2 p-8 sm:p-10 overflow-y-auto max-h-[80vh]">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Inquire Now</h2>
            <button onClick={onClose} className="hidden sm:flex">
              <X className="w-6 h-6 text-gray-600 hover:text-gray-900 transition" />
            </button>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {[
              { label: "Customer Name*", name: "name", type: "text" },
              { label: "Mobile No*", name: "mobile", type: "tel" },
              { label: "Email*", name: "email", type: "email" },
            ].map((field) => (
              <div key={field.name}>
                {field.name === "mobile" ? (
                  <div className="flex gap-3">
                    {/* Country Code Dropdown */}
                    <select
                      name="country_code"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="w-32 px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-orange-400 focus:ring-2 focus:ring-orange-300 outline-none shadow-sm hover:shadow-md transition text-gray-800 font-medium bg-white"
                    >
                      <option value="">Select</option>
                      {countryCodes.map((c) => (
                        <option key={c.value} value={c.value}>
                          +{c.label}
                        </option>
                      ))}
                    </select>

                    {/* Mobile Input */}
                    <input
                      type={field.type}
                      maxLength={10}
                      name={field.name}
                      value={form[field.name]}
                      onChange={handleChange}
                      placeholder={field.label}
                      className={`flex-1 px-5 py-3 border-2 rounded-2xl outline-none shadow-sm transition
            ${
              errors[field.name]
                ? "border-red-400 focus:border-red-400"
                : "border-gray-200 focus:border-orange-400"
            }`}
                    />
                  </div>
                ) : (
                  // Normal input for name and email
                  <input
                    type={field.type}
                    name={field.name}
                    value={form[field.name]}
                    onChange={handleChange}
                    placeholder={field.label}
                    className={`w-full px-5 py-3 border-2 rounded-2xl outline-none shadow-sm transition
          ${
            errors[field.name]
              ? "border-red-400 focus:border-red-400"
              : "border-gray-200 focus:border-orange-400"
          }`}
                  />
                )}

                {errors[field.name] && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors[field.name]}
                  </p>
                )}
              </div>
            ))}

            {/* Meal Type */}
            <select
              name="subject"
              value={form.subject}
              onChange={handleChange}
              className="w-full px-5 py-3 border-2 border-gray-200 rounded-2xl focus:border-orange-400 focus:ring-2 focus:ring-orange-300 outline-none shadow-sm hover:shadow-md transition text-gray-800 font-medium bg-white"
            >
              <option value="">Select Meal</option>

              {mealsData?.map((meal) => (
                <option key={meal.meal_id} value={meal.meal_id}>
                  {meal.meal_name}
                </option>
              ))}

              <option value="Other">Other</option>
            </select>

            {errors.subject && (
              <p className="text-red-500 text-sm mt-1">{errors.subject}</p>
            )}

            {isOther && (
              <input
                type="text"
                name="other"
                value={form.other}
                onChange={handleChange}
                placeholder="Enter other subject"
                className="w-full px-5 py-3 border-2 border-gray-200 rounded-2xl focus:border-orange-400 focus:ring-2 focus:ring-orange-300 outline-none shadow-sm hover:shadow-md transition text-gray-800 font-medium"
              />
            )}

            {/* Description */}
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={4}
              placeholder="Description"
              className="w-full px-5 py-3 border-2 border-gray-200 rounded-2xl focus:border-orange-400 focus:ring-2 focus:ring-orange-300 outline-none shadow-sm hover:shadow-md transition text-gray-800 font-medium resize-none"
            ></textarea>

            {/* Submit */}
            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={isProviderLoading}
                className="px-8 py-3  bg-gradient-to-r from-orange-400 to-orange-600  text-white rounded-2xl font-semibold
                  hover:scale-105 hover:shadow-xl transition-transform
                  disabled:opacity-70 disabled:cursor-not-allowed
                  flex items-center justify-center gap-2"
              >
                {isProviderLoading ? (
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                ) : (
                  "Submit"
                )}
              </button>
              
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EnqForm;
